import seedrandom, { prng } from 'seedrandom';

export default class RandomSeed {
    private rng: prng;

    constructor(seed?: any) {
        this.seed(seed);
    }

    /**
     * Apply a seed for predictable numbers.
     * @param seed
     */
    public seed(seed?: any): void {
        this.rng = seedrandom(seed);
    }

    /**
     * Return a random number between min and max.
     * @param min Minimum boundary for the random value, included.
     * @param max Maximum boundary for the random value, included.
     */
    public value(min: number = 0, max: number = 1): number {
        return this.rng() * (max - min) + min;
    }

    /**
     * Return a random integer between min and max.
     * @param min Minimum boundary for the random value, included.
     * @param max Maximum boundary for the random value, included.
     */
    public int(min: number, max: number): number {
        min = Math.ceil(min);
        max = Math.floor(max);
        return Math.floor(this.value() * (max - min + 1)) + min;
    }

    /**
     * Return a random sign.
     */
    public sign(): number {
        return this.int(0, 1) * 2 - 1;
    }

    /**
     * Return a random ID.
     */
    public ID(): string {
        return this.value()
            .toString(36)
            .substr(2, 9);
    }

    /**
     * Return a random index from an array of numbers.
     * @param weights The array of weights.
     */
    public getRandomWeighted(weights: number[]): number {
        const totalWeight = weights.reduce((p: number, v: number) => p + v, 0);
        const num = this.value(0, totalWeight);

        let sum = 0;
        for (let i = 0; i < weights.length; i++) {
            sum += weights[i];

            if (num <= sum) {
                return i;
            }
        }
    }

    /**
     * Pick a random item from an array.
     * @param array The array to pick an item.
     */
    public getRandomItem<T>(array: T[]): T {
        return array[this.int(0, array.length - 1)];
    }

    /**
     * Pick random items from an array.
     * @param array The array to pick the items.
     * @param amount Specifies how many items should be picked.
     */
    public getRandomItems<T>(array: T[], amount: number): T[] {
        return this.shuffle([...array]).slice(0, amount);
    }

    /**
     * Shuffle the array.
     * @param array The array to be shuffled.
     * @param noRepeat Force the array to derange (values are forced into a new position).
     */
    public shuffle<T>(array: T[], noRepeat: boolean = false): T[] {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(this.value() * (i + (noRepeat ? 0 : 1)));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }
}

// Default (global) random seed.
export const Random = new RandomSeed();
